'use client';

import { useEffect } from 'react';
import Link from 'next/link';
import { errorLogger } from '@/lib/errorLogger';

interface Props {
  error: Error & { digest?: string };
  reset: () => void;
  title?: string;
  description?: string;
  showHomeLink?: boolean;
  homeHref?: string;
  homeLabel?: string;
}

export default function ErrorBoundaryFallback({
  error,
  reset,
  title = 'Something went sideways',
  description = 'The operation failed. We captured the details for review — try again or head back to base.',
  showHomeLink = true,
  homeHref = '/',
  homeLabel = 'Return to dashboard',
}: Props) {
  useEffect(() => {
    errorLogger.captureError(error, {
      source: 'error-boundary',
      digest: error.digest,
      url: typeof window !== 'undefined' ? window.location.href : undefined,
    });
  }, [error]);

  return (
    <div className="min-h-[60vh] flex items-center justify-center px-6 py-12">
      <div className="max-w-md w-full border border-red-500/40 bg-red-950/40 rounded-lg p-6 text-red-100">
        <p className="text-xs font-mono tracking-widest text-red-300/80 uppercase mb-2">
          Mission Fault
        </p>
        <h2 className="text-xl font-bold mb-3">{title}</h2>
        <p className="text-sm text-red-100/80 mb-5 leading-relaxed">{description}</p>

        {error.digest && (
          <p className="text-xs font-mono text-red-300/60 mb-4">
            Ref: {error.digest}
          </p>
        )}

        <div className="flex flex-col sm:flex-row gap-2">
          <button
            onClick={reset}
            className="flex-1 bg-red-500 hover:bg-red-400 text-red-950 font-bold py-2.5 px-4 rounded transition"
          >
            Try again
          </button>
          {showHomeLink && (
            <Link
              href={homeHref}
              className="flex-1 text-center border border-red-500/60 text-red-200 hover:bg-red-500/10 font-semibold py-2.5 px-4 rounded transition"
            >
              {homeLabel}
            </Link>
          )}
        </div>
      </div>
    </div>
  );
}
